export type SiteConfigResponse = {
  ok: true;
  site: "cn" | "en";
  locale: string;
  canonicalUrl: string;
  alternateUrl: string;
  products: ProductOffer[];
  features: {
    aiModels: ["deepseek-v4-flash", "deepseek-v4-pro"];
    googleLogin: boolean;
    passwordReset: boolean;
    payment: boolean;
    paymentProvider: "pancake";
    paymentEnvironment: "test" | "prod";
    turnstileSiteKey: string;
    analytics: { gtmContainerId: string; consentMode: "basic" };
  };
};

export type ProductOffer = {
  sku:
    | "cn_single_generation"
    | "cn_ten_generations"
    | "en_single_generation"
    | "en_ten_generations";
  credits: 1 | 10;
  amountMinor: number;
  displayAmount: string;
  currency: "cny" | "usd";
};

export type MeResponse = {
  ok: true;
  user: { id: string; email: string; name: string } | null;
  credits: number;
};

export type ResultName = {
  fullName: string;
  givenName: string;
  pinyin: string;
  pronunciation: string;
  characters: Array<{ character: string; meaning: string }>;
  overallMeaning: string;
  recommendation: string;
  source: { title: string; quote: string; author: string; dynasty: string };
  soundAndForm: string;
  usage: string;
  originalNameConnection: string;
  risks: string[];
  score: number;
};

export type GenerationRequest = {
  id: string;
  site: "cn" | "en";
  status: "queued" | "processing" | "retrying" | "completed" | "failed_refunded";
  errorCode: string | null;
  errorMessage: string | null;
  createdAt: number;
  updatedAt: number;
  result: { summary: string; names: ResultName[] } | null;
};

export type OrderRecord = {
  id: string;
  site: "cn" | "en";
  sku: string;
  amountMinor: number;
  currency: string;
  credits: number;
  refundedAmountMinor: number;
  status: string;
  createdAt: number;
  updatedAt: number;
};

export type AccountSummary = {
  ok: true;
  user: {
    id: string;
    name: string;
    email: string;
    image: string | null;
    emailVerified: boolean;
    createdAt: number;
  };
  balances: { cn: number; en: number };
  providers: string[];
  counts: { names: number; favorites: number; orders: number; refunds: number };
  isAdmin: boolean;
};

export type CreditLedgerRecord = {
  id: string;
  site: "cn" | "en";
  delta: number;
  reason: string;
  referenceId: string;
  createdAt: number;
};

export type RefundRequestRecord = {
  id: string;
  orderId: string;
  site: "cn" | "en";
  requestedAmountMinor: number;
  approvedAmountMinor: number | null;
  currency: string;
  reason: string;
  details: string;
  status: "submitted" | "reviewing" | "provider_pending" | "succeeded" | "failed" | "rejected";
  providerStatus: string | null;
  adminNote: string | null;
  createdAt: number;
  updatedAt: number;
};

export type CheckoutAttribution = {
  analyticsClientId?: string;
  analyticsSessionId?: string;
  gclid?: string;
  gbraid?: string;
  wbraid?: string;
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  utmContent?: string;
  utmTerm?: string;
  analyticsConsent: boolean;
  marketingConsent: boolean;
};

export type PagedResponse<T> = { ok: true; items: T[]; nextCursor: string | null };

export type FavoriteRecord = { requestId: string; fullName: string; createdAt: number };
export type AccountFavoriteRecord = FavoriteRecord & {
  id: string;
  site: "cn" | "en";
  status: string;
};
export type SurnameFigure = {
  name: string;
  surnameHanzi: string;
  nameEn: string | null;
  titleZh: string | null;
  titleEn: string | null;
  era: string | null;
  introZh: string | null;
  introEn: string | null;
};

export type SurnameRecord = {
  hanzi: string;
  pinyin: string;
  rank: number | null;
  meaningZh: string;
  meaningEn: string;
  originZh?: string | null;
  originEn?: string | null;
  famousZh?: string | null;
  famousEn?: string | null;
  distributionZh?: string | null;
  distributionEn?: string | null;
  storyZh?: string | null;
  storyEn?: string | null;
  figures?: SurnameFigure[];
};
export type NameAnalysis = {
  fullName: string;
  surname: string;
  givenName: string;
  characters: Array<{ character: string; meaningZh: string; meaningEn: string }>;
  source: null | { title: string; quote: string; author: string; dynasty: string };
  tags: string[];
  risks: string[];
  note: string;
  famousCase: null | {
    fullName: string;
    eraZh: string;
    eraEn: string;
    roleZh: string;
    roleEn: string;
    knownForZh: string;
    knownForEn: string;
    referenceTitle: string;
    referenceUrl: string;
  };
};

export type AiNameAnalysis = {
  summary: string;
  characterInteraction: string;
  soundAndForm: string;
  usage: string;
  culturalContext: string;
  cautions: string[];
  confidence: "high" | "medium" | "low";
  evidenceNote: string;
};

export type AiNameAnalysisState = {
  status:
    | "signin_required"
    | "queued"
    | "processing"
    | "retrying"
    | "completed"
    | "failed"
    | "limit_reached";
  requestId?: string;
  result?: AiNameAnalysis | null;
  model?: string | null;
  error?: string | null;
  message?: string;
};

export type NameAnalysisResponse = { ok: true; analysis: NameAnalysis; ai: AiNameAnalysisState };

export class ApiRequestError extends Error {
  constructor(
    message: string,
    public readonly status: number,
  ) {
    super(message);
    this.name = "ApiRequestError";
  }
}

async function jsonFetch<T>(url: string, init?: RequestInit): Promise<T> {
  const response = await fetch(url, {
    ...init,
    headers: { "Content-Type": "application/json", ...init?.headers },
  });
  const body = (await response.json()) as T & { error?: string };
  if (!response.ok)
    throw new ApiRequestError(body.error ?? `Request failed (${response.status})`, response.status);
  return body;
}

export async function loadSiteConfig(): Promise<SiteConfigResponse> {
  return jsonFetch<SiteConfigResponse>("/api/config");
}

export async function loadMe(): Promise<MeResponse> {
  return jsonFetch<MeResponse>("/api/me");
}

export async function createCheckout(
  sku: ProductOffer["sku"],
  attribution?: CheckoutAttribution,
): Promise<string> {
  const result = await jsonFetch<{ ok: true; url: string }>("/api/billing/checkout", {
    method: "POST",
    body: JSON.stringify({ sku, attribution }),
  });
  return result.url;
}

export async function openCheckout(
  sku: ProductOffer["sku"],
  attribution?: CheckoutAttribution,
): Promise<void> {
  const checkoutWindow = window.open("about:blank", "_blank");
  if (!checkoutWindow) throw new Error("浏览器阻止了收银台窗口，请允许弹出窗口后重试");
  checkoutWindow.opener = null;
  try {
    const url = await createCheckout(sku, attribution);
    checkoutWindow.location.replace(url);
  } catch (error) {
    checkoutWindow.close();
    throw error;
  }
}

export async function createGeneration(
  input: Record<string, unknown>,
  turnstileToken: string,
): Promise<string> {
  const result = await jsonFetch<{ ok: true; requestId: string }>("/api/names/generate", {
    method: "POST",
    headers: { "Idempotency-Key": crypto.randomUUID().replaceAll("-", "") },
    body: JSON.stringify({ ...input, turnstileToken }),
  });
  return result.requestId;
}

export async function loadGeneration(requestId: string): Promise<GenerationRequest> {
  const result = await jsonFetch<{ ok: true; request: GenerationRequest }>(
    `/api/names/${requestId}`,
  );
  return result.request;
}

export async function regenerate(requestId: string, direction: string): Promise<string> {
  const result = await jsonFetch<{ ok: true; requestId: string }>(
    `/api/names/${requestId}/regenerate`,
    {
      method: "POST",
      headers: { "Idempotency-Key": crypto.randomUUID().replaceAll("-", "") },
      body: JSON.stringify({ direction }),
    },
  );
  return result.requestId;
}

export async function loadHistory(): Promise<GenerationRequest[]> {
  const result = await jsonFetch<{ ok: true; requests: GenerationRequest[] }>("/api/names/history");
  return result.requests;
}

export async function loadOrders(): Promise<OrderRecord[]> {
  const result = await jsonFetch<{ ok: true; orders: OrderRecord[] }>("/api/orders");
  return result.orders;
}

function accountQuery(params: {
  site?: "cn" | "en";
  status?: string;
  cursor?: string;
  limit?: number;
}): string {
  const query = new URLSearchParams();
  if (params.site) query.set("site", params.site);
  if (params.status) query.set("status", params.status);
  if (params.cursor) query.set("cursor", params.cursor);
  if (params.limit) query.set("limit", String(params.limit));
  const value = query.toString();
  return value ? `?${value}` : "";
}

export async function loadAccountSummary(): Promise<AccountSummary> {
  return jsonFetch<AccountSummary>("/api/account/summary");
}

export async function updateAccountProfile(name: string): Promise<void> {
  await jsonFetch<{ ok: true }>("/api/account/profile", {
    method: "PATCH",
    body: JSON.stringify({ name }),
  });
}

export async function loadAccountNames(
  params: Parameters<typeof accountQuery>[0] = {},
): Promise<PagedResponse<GenerationRequest>> {
  return jsonFetch(`/api/account/names${accountQuery(params)}`);
}

export async function loadAccountOrders(
  params: Parameters<typeof accountQuery>[0] = {},
): Promise<PagedResponse<OrderRecord>> {
  return jsonFetch(`/api/account/orders${accountQuery(params)}`);
}

export async function loadAccountRefunds(
  params: Parameters<typeof accountQuery>[0] = {},
): Promise<PagedResponse<RefundRequestRecord>> {
  return jsonFetch(`/api/account/refunds${accountQuery(params)}`);
}

export async function loadAccountCredits(
  params: Parameters<typeof accountQuery>[0] = {},
): Promise<PagedResponse<CreditLedgerRecord>> {
  return jsonFetch(`/api/account/credits${accountQuery(params)}`);
}

export async function loadAccountFavorites(
  params: Parameters<typeof accountQuery>[0] = {},
): Promise<PagedResponse<AccountFavoriteRecord>> {
  return jsonFetch(`/api/account/favorites${accountQuery(params)}`);
}

export async function submitRefundRequest(params: {
  orderId: string;
  reason: "duplicate" | "billing_error" | "not_delivered" | "other";
  details: string;
}): Promise<RefundRequestRecord> {
  const result = await jsonFetch<{ ok: true; refund: RefundRequestRecord }>(
    "/api/account/refunds",
    { method: "POST", body: JSON.stringify(params) },
  );
  return result.refund;
}

export async function downloadAccountExport(): Promise<void> {
  const response = await fetch("/api/account/export");
  if (!response.ok) {
    const body = (await response.json()) as { error?: string };
    throw new ApiRequestError(body.error ?? "数据导出失败", response.status);
  }
  const url = URL.createObjectURL(await response.blob());
  const link = document.createElement("a");
  link.href = url;
  link.download = `jieyi-account-${new Date().toISOString().slice(0, 10)}.json`;
  link.click();
  window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

export async function loadFavorites(): Promise<FavoriteRecord[]> {
  const result = await jsonFetch<{ ok: true; favorites: FavoriteRecord[] }>("/api/favorites");
  return result.favorites;
}

export async function loadSurnames(query = ""): Promise<SurnameRecord[]> {
  const result = await jsonFetch<{ ok: true; surnames: SurnameRecord[] }>(
    `/api/surnames${query ? `?q=${encodeURIComponent(query)}` : ""}`,
  );
  return result.surnames;
}

export async function loadSurname(hanzi: string): Promise<SurnameRecord> {
  const result = await jsonFetch<{ ok: true; surname: SurnameRecord }>(
    `/api/surnames/${encodeURIComponent(hanzi)}`,
  );
  return result.surname;
}

export async function loadFigure(name: string): Promise<{
  figure: SurnameFigure;
  surname: SurnameRecord;
  siblings: SurnameFigure[];
}> {
  return jsonFetch(`/api/figures/${encodeURIComponent(name)}`);
}

export async function analyzeName(name: string): Promise<NameAnalysisResponse> {
  return jsonFetch<NameAnalysisResponse>("/api/name-meaning", {
    method: "POST",
    headers: { "Idempotency-Key": crypto.randomUUID().replaceAll("-", "") },
    body: JSON.stringify({ name }),
  });
}

export async function loadNameAnalysis(requestId: string): Promise<AiNameAnalysisState> {
  const result = await jsonFetch<{ ok: true; ai: AiNameAnalysisState }>(
    `/api/name-meaning/${requestId}`,
  );
  return result.ai;
}

export async function deleteAccount(): Promise<void> {
  await jsonFetch<{ ok: true }>("/api/account", {
    method: "DELETE",
    body: JSON.stringify({ confirmation: "DELETE" }),
  });
}

export async function toggleFavorite(requestId: string, fullName: string): Promise<boolean> {
  const result = await jsonFetch<{ ok: true; favorite: boolean }>("/api/favorites/toggle", {
    method: "POST",
    body: JSON.stringify({ requestId, fullName }),
  });
  return result.favorite;
}
